/**
 * @fileoverview Multi unidades — a casca da página: cabeçalho, abas Dashboard e
 * SMS Alerts, e o painel de cada uma (MultiUnitV2 e SmsAlertsView).
 *
 * Até 28/10/2026 esta casca gerava um conjunto sintético (10 mil conversas,
 * 50 mil mensagens) para as visões antigas (overview, charts, detalhe de
 * unidade), o selo "Dev", o "Última atualização" e o "Exportar" de mentira.
 * Tudo isso saiu: os números da tela vêm do servidor (MultiUnitV2).
 */

import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission from "../../hooks/usePermission";
import { isInternalOnly } from "../../utils/accessScope";
import MultiUnitV2 from "./v2/MultiUnitV2";
import { makeStyles } from "@material-ui/core/styles";
import { BellRing, Building, Table as TableIcon } from "lucide-react";
import SmsAlertsView from "./SmsAlertsView";

const getThemeTokens = (theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    isDark,
    pageBg: isDark ? "#0A0A0A" : "#F7F8FA",
    cardBg: isDark ? "rgba(14,16,22,0.85)" : "#ffffff",
    // A mesma borda dos cartões do painel (v2/theme.js).
    cardBorder: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.16)",
    textPrimary: isDark ? "#F2F4F8" : "#0F172A",
    textSecondary: isDark ? "#9CA3AF" : "#475569",
    accent: isDark ? "#7C9AFF" : "#3D5CFD",
    accentSoft: isDark ? "rgba(124,154,255,0.14)" : "rgba(61,92,253,0.10)",
    accentSoftStrong: isDark ? "rgba(124,154,255,0.22)" : "rgba(61,92,253,0.16)",
  };
};

const useStyles = makeStyles((theme) => {
  const c = getThemeTokens(theme);
  return {
    pageWrap: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      // Fundo liso, o mesmo do painel: sem o brilho azul, que deixava o painel
      // como um quadro preto no escuro.
      backgroundColor: c.pageBg,
      fontFamily: `"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif`,
      color: c.textPrimary,
      "&::-webkit-scrollbar": { width: 8 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        background: c.isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.16)",
        borderRadius: 4,
      },
    },
    page: {
      width: "100%",
      maxWidth: 1600,
      margin: "0 auto",
      padding: "20px 24px 60px",
      [theme.breakpoints.down("sm")]: { padding: "14px 12px 80px" },
    },
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      marginBottom: 18,
      flexWrap: "wrap",
    },
    headerLeft: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    titleRow: { display: "flex", alignItems: "center", gap: 12 },
    titlePillIcon: {
      width: 38,
      height: 38,
      borderRadius: 12,
      background: `linear-gradient(135deg, ${c.accentSoftStrong}, ${c.accentSoft})`,
      color: c.accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: c.isDark
        ? `inset 0 0 0 1px rgba(124,154,255,0.18)`
        : `inset 0 0 0 1px rgba(61,92,253,0.18)`,
    },
    titleStack: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    title: {
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      lineHeight: 1.1,
    },
    subtitle: {
      fontSize: 12.5,
      color: c.textSecondary,
      lineHeight: 1.4,
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    tabBar: {
      display: "inline-flex",
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: 10,
      padding: 4,
      gap: 2,
    },
    tabBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "7px 12px",
      borderRadius: 6,
      border: "none",
      background: "transparent",
      color: c.textSecondary,
      fontSize: 12.5,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.15s ease",
      "&:hover": { color: c.textPrimary },
    },
    tabBtnActive: {
      background: c.accentSoftStrong,
      color: c.accent,
    },
  };
});

const MultiUnit = () => {
  const classes = useStyles();

  // ?tab=sms abre o SMS; ?tab=overview|charts|dashboard (endereços antigos,
  // como o /dashboard-geral, que redireciona para ?tab=charts) e o resto, o
  // Dashboard.
  const initialView = (() => {
    try {
      if (new URLSearchParams(window.location.search).get("tab") === "sms") return "sms";
    } catch (_) {
      /* sem window */
    }
    return "dashboard";
  })();
  const [view, setView] = useState(initialView); // dashboard | sms

  const { user } = useContext(AuthContext);
  // Os alertas por SMS são opções da empresa: ler e gravar pedem settings.manage
  // (hoje, o admin fora do "Apenas Interno", como a API). Sem ela, a aba não
  // aparece: mostraria o padrão como se fosse o gravado, e salvar daria erro.
  const { can, loaded } = usePermission();
  const canManageSms = can("settings.manage", user?.profile === "admin" && !isInternalOnly(user));
  // Sem a permissão, o endereço ?tab=sms abre o Dashboard (a aba nem aparece).
  useEffect(() => {
    if (loaded && view === "sms" && !canManageSms) setView("dashboard");
  }, [loaded, view, canManageSms]);

  return (
    <div className={classes.pageWrap}>
      <div className={classes.page}>
        {view === "sms" && canManageSms ? (
          <SmsAlertsView onBack={() => setView("dashboard")} />
        ) : (
          <>
            <div className={classes.header}>
              <div className={classes.headerLeft}>
                <div className={classes.titleRow}>
                  <span className={classes.titlePillIcon}>
                    <Building size={18} strokeWidth={2} />
                  </span>
                  <div className={classes.titleStack}>
                    <div className={classes.title}>Multi unidades</div>
                    <div className={classes.subtitle}>
                      Visão geral de produtividade e atendimento por unidade
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.headerActions}>
                <div className={classes.tabBar}>
                  <button
                    type="button"
                    className={`${classes.tabBtn} ${classes.tabBtnActive}`}
                    onClick={() => setView("dashboard")}
                  >
                    <TableIcon size={13} />
                    Dashboard
                  </button>
                  {canManageSms && (
                    <button type="button" className={classes.tabBtn} onClick={() => setView("sms")}>
                      <BellRing size={13} />
                      SMS Alerts
                    </button>
                  )}
                </div>
              </div>
            </div>
            <MultiUnitV2 groupName={user?.company?.groupName || ""} />
          </>
        )}
      </div>
    </div>
  );
};

export default MultiUnit;
